/**
 * useAvatarSrc Hook
 *
 * Turns a stored profile-picture reference into something an `<img>` can actually render.
 */

import { useEffect, useState } from "react";
import { uploadService } from "@/services/upload.service";

/** Prefix of the app-relative path returned by `POST /uploads/file`. */
const UPLOADS_PATH_PREFIX = "/api/v1/uploads/files/";

/**
 * Resolves `profilePictureUrl` for display. Three shapes reach this hook:
 *
 * - **Absolute URL** — pictures carried over from an external identity provider. Used as-is.
 * - **Upload path** (`/api/v1/uploads/files/…`) — the file is behind `[Authorize]`, and a plain
 *   `<img src>` sends no bearer token, so it is fetched through the authenticated api client and
 *   handed to the DOM as an object URL instead.
 * - **Anything else** — raw storage keys (`avatars/<tenant>/…`) written by the old `/auth/avatar`
 *   endpoint. Those resolve against the SPA origin and 404, so they render as the initials
 *   fallback rather than a broken image.
 *
 * The object URL is revoked when the reference changes and on unmount.
 */
export function useAvatarSrc(
  profilePictureUrl?: string | null,
): string | undefined {
  const [objectUrl, setObjectUrl] = useState<string>();

  const isUploadPath = !!profilePictureUrl?.startsWith(UPLOADS_PATH_PREFIX);

  useEffect(() => {
    if (!isUploadPath || !profilePictureUrl) {
      setObjectUrl(undefined);
      return;
    }

    let revoked = false;
    let created: string | undefined;

    uploadService
      .downloadFile(profilePictureUrl)
      .then((url) => {
        // The reference changed (or the component unmounted) while the fetch was in flight:
        // adopting this URL would leak it and show the previous avatar.
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjectUrl(url);
      })
      .catch(() => {
        // A missing or forbidden avatar is not worth a toast — fall back to the initials.
        if (!revoked) setObjectUrl(undefined);
      });

    return () => {
      revoked = true;
      if (created) {
        URL.revokeObjectURL(created);
        // Drop it from state in the same pass. Leaving a revoked URL on the <img> paints a
        // broken-image icon until the next fetch lands; the initials fallback is the better
        // thing to show for those few hundred milliseconds.
        setObjectUrl(undefined);
      }
    };
  }, [isUploadPath, profilePictureUrl]);

  if (!profilePictureUrl) return undefined;
  if (isUploadPath) return objectUrl;
  return /^https?:\/\//i.test(profilePictureUrl) ? profilePictureUrl : undefined;
}
